import { AsyncPipe } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CommonService } from '@appShared/services/common.service';
import {
  ShareholderEditSectionData,
  ShareholderEditSection_,
  AdminService
} from '@appShared/services/admin.service';
import {
   ConfirmModalComponent,
   ConfirmService,
   ConfirmTemplateDirective
} from '@appShared/components/confirm-modal-and-service';
import { AutofocusDirective } from '@appShared/directives/auto-focus.directive';
import { IEmployeeSearchResult } from '@appShared/interfaces/[Interface-based]/employee-search-result.interface';
import { IShareholder } from '@appShared/interfaces/[Model-based]/shareholder.interface';
import { ShareholderPinModalComponent } from './shareholder-pin-modal/shareholder-pin-modal.component';

@Component({
  selector: 'app-shareholder-new',
   templateUrl: './shareholder-new.component.html',
   imports: [
      FormsModule,
      ReactiveFormsModule,
      AsyncPipe,
      AutofocusDirective,
      ConfirmTemplateDirective,
      ConfirmModalComponent
   ]
})
export class ShareholderNewComponent implements OnInit, OnDestroy {
  @Input() shareholder: IShareholder;
  @Output() shareholderSectionUpdated = new EventEmitter();
  private _ngDestroyed$ = new Subject();
  searchedTerm: string;
  searching: boolean;
  hasSearched: boolean;
  employeeSearchTerm: FormControl;
  employees$: Observable<IEmployeeSearchResult[]>;
  mouseoverNewShareholder: boolean;

  constructor(
    private _adminService: AdminService,
    private _commonService: CommonService,
    private _confirmService: ConfirmService,
    private _modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.employeeSearchTerm = new FormControl();

    // https://codecraft.tv/courses/angular/http/http-with-observables/
    this.employees$ = this.employeeSearchTerm.valueChanges.pipe(
      takeUntil(this._ngDestroyed$),
      debounceTime(400),
      distinctUntilChanged(),
      filter(searchTerm => searchTerm.length > 1) /* more than 1 character */,
      tap(_ => (this.searching = true)),
      switchMap(term => this._adminService.employeeSearch(term)),
      tap(_ => (this.searching = false))
    );
  }

  ngOnDestroy(): void {
    this._ngDestroyed$.next(true);
    this._ngDestroyed$.complete();
  }
  /*
   * private methods
   * */

  /*
   * public methods
   * */

  createShareholder(employee: IEmployeeSearchResult): void {
    const employeeName =
      employee.displayName ||
      (employee.firstName
        ? employee.lastName
          ? `${employee.firstName} ${employee.lastName}`
          : employee.firstName
        : employee.lastName || '');
    const company = employee.company;
    const department = employee.department;
    const companyDepartment = !!(company && department)
      ? `${employee.company} / ${employee.department}`
      : company
        ? employee.company
        : employee.department || '';

    this._confirmService
      .confirm({
        title: 'Verify New Shareholder!',
        message: `You are creationg a new shareholder:
            <p class='ps-3'><strong class='text-site-primary'>${employeeName}<br/>${companyDepartment}
            </strong><p>
            Are you sure?`
      })
      .then(() => {
        const shareholderPinModalRef = this._modalService.open(
          ShareholderPinModalComponent
        );

        const shareholderPinComponent =
          shareholderPinModalRef.componentInstance;
        shareholderPinComponent.employee = { ...employee };
        shareholderPinComponent.shareholderSectionUpdated = (
          shareholder: IShareholder
        ) => {
          this.shareholderSectionUpdated.emit({
            shareholderEditSection: ShareholderEditSection_.newEmployeeInfo,
            shareholder,
            refresh: true
          } as ShareholderEditSectionData);
        };
      })
      .catch(() => {
        /* Do Nothing */
      });
  }
}
